// pages/public/RestaurantesPage.jsx
import { useEffect, useState, useMemo } from 'react';
import PublicLayout from '../../layout/PublicLayout';
import { getEstablecimientosPublicos, getRestauranteActual } from '../../api/public';
import { useTypewriterColored } from '../../hooks/useTypewriterColored';
import { MapPin, Phone, Clock, Navigation } from 'lucide-react';
import MultiLocationMap from '../../components/MultiLocationMap';

const RestaurantesPage = () => {
  const [establecimientos, setEstablecimientos] = useState([]);
  const [restaurante, setRestaurante] = useState(null);
  const [loading, setLoading] = useState(true);

  // Efecto typewriter para "Nuestras Ubicaciones"
  // "Nuestras " tiene 9 caracteres (incluyendo el espacio)
  const { part1, part2, isComplete } = useTypewriterColored('Nuestras Ubicaciones', 9, 100, 50, 2000);

  // Memorizar las ubicaciones para el mapa (evita re-renders por el typewriter)
  const mapLocations = useMemo(() => {
    return establecimientos.map((est, index) => ({
      name: index === 0 ? 'Matriz' : `Sucursal ${index}`,
      address: est.direstablecimiento,
      label: index === 0 ? 'M' : `${index}`,
      lat: est.latitud,
      lng: est.longitud,
    }));
  }, [establecimientos]);

  useEffect(() => {
    const loadData = async () => {
      try {
        // Cargar información del restaurante
        const restauranteData = await getRestauranteActual();
        setRestaurante(restauranteData);

        // Cargar establecimientos
        const establecimientosData = await getEstablecimientosPublicos();
        setEstablecimientos(establecimientosData);
      } catch (error) {
        console.error('Error cargando datos:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  // Abrir en Google Maps
  const abrirEnMaps = (direccion) => {
    const query = encodeURIComponent(direccion);
    window.open(`https://www.google.com/maps/search/?api=1&query=${query}`, '_blank');
  };

  // Formatear horario
  const formatearHorario = (apertura, cierre) => {
    if (!apertura || !cierre) return 'Consultar horarios';
    return `${apertura.substring(0, 5)} - ${cierre.substring(0, 5)}`;
  };

  return (
    <PublicLayout>
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-red-600 to-orange-500 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <MapPin className="w-16 h-16 mx-auto mb-4" />
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">
              <span className="text-white">{part1}</span>
              <span className="text-yellow-300">
                {part2}
                {!isComplete && <span className="animate-pulse">|</span>}
              </span>
            </h1>
            <p className="text-xl text-red-50 max-w-2xl mx-auto">
              Encuentra el establecimiento más cercano a ti
            </p>
          </div>
        </div>
      </section>

      {/* Información del Restaurante */}
      {restaurante && (
        <section className="py-12 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-2xl p-8 shadow-lg">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-4">
                    {restaurante.nombre_comercial}
                  </h2>
                  {restaurante.eslogan && (
                    <p className="text-xl text-gray-600 italic mb-6">
                      "{restaurante.eslogan}"
                    </p>
                  )}
                  {restaurante.historia && (
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">Nuestra Historia</h3>
                      <p className="text-gray-600 leading-relaxed">
                        {restaurante.historia}
                      </p>
                    </div>
                  )}
                </div>

                <div className="space-y-4">
                  {restaurante.telefono && (
                    <div className="flex items-start gap-3">
                      <Phone className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                      <div>
                        <p className="font-semibold text-gray-900">Teléfono</p>
                        <a href={`tel:${restaurante.telefono}`} className="text-red-600 hover:text-red-700">
                          {restaurante.telefono}
                        </a>
                      </div>
                    </div>
                  )}

                  {restaurante.correo && (
                    <div className="flex items-start gap-3">
                      <svg className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      <div>
                        <p className="font-semibold text-gray-900">Correo</p>
                        <a href={`mailto:${restaurante.correo}`} className="text-red-600 hover:text-red-700">
                          {restaurante.correo}
                        </a>
                      </div>
                    </div>
                  )}

                  {(restaurante.horario_apertura && restaurante.horario_cierre) && (
                    <div className="flex items-start gap-3">
                      <Clock className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                      <div>
                        <p className="font-semibold text-gray-900">Horario</p>
                        <p className="text-gray-600">
                          {formatearHorario(restaurante.horario_apertura, restaurante.horario_cierre)}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Establecimientos */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Nuestros <span className="text-red-600">Establecimientos</span>
          </h2>

          {loading ? (
            <div className="text-center py-20">
              <div className="inline-block animate-spin rounded-full h-16 w-16 border-b-4 border-red-600"></div>
              <p className="mt-4 text-gray-600 text-lg">Cargando ubicaciones...</p>
            </div>
          ) : establecimientos.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
              {establecimientos.map((establecimiento, index) => (
                <div
                  key={establecimiento.id}
                  className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300"
                >
                  {/* Header con nombre */}
                  <div className="bg-gradient-to-r from-red-600 to-orange-500 text-white px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="bg-white bg-opacity-20 rounded-full w-14 h-14 flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-7 h-7" />
                      </div>
                      <div>
                        <p className="text-xs text-red-100 uppercase tracking-wide mb-1">
                          {index === 0 ? 'Casa Matriz' : `Sucursal ${index}`}
                        </p>
                        <h3 className="text-xl font-bold">
                          {index === 0 ? 'Matriz' : `Sucursal ${index}`}
                        </h3>
                      </div>
                    </div>
                  </div>

                  {/* Contenido */}
                  <div className="p-6">
                    {/* Dirección */}
                    <div className="mb-6">
                      <div className="flex items-start gap-3">
                        <MapPin className="w-5 h-5 text-red-600 flex-shrink-0 mt-1" />
                        <div className="flex-1">
                          <p className="font-semibold text-gray-900 mb-2">Dirección</p>
                          <p className="text-gray-600 leading-relaxed">
                            {establecimiento.direstablecimiento}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Botón de Google Maps */}
                    <button
                      onClick={() => abrirEnMaps(establecimiento.direstablecimiento)}
                      className="w-full bg-red-600 text-white px-6 py-4 rounded-lg font-semibold hover:bg-red-700 transition-colors shadow-md hover:shadow-lg flex items-center justify-center gap-2 text-base"
                    >
                      <Navigation className="w-5 h-5" />
                      Ver en Google Maps
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <MapPin className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                No hay establecimientos registrados
              </h3>
              <p className="text-gray-600">
                Pronto abriremos nuevas ubicaciones
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Mapa General - Dinámico basado en los establecimientos */}
      {establecimientos.length > 0 && (
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              Encuéntranos en el <span className="text-red-600">Mapa</span>
            </h2>

            {/* Mapa interactivo con múltiples marcadores */}
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <MultiLocationMap locations={mapLocations} />
            </div>
            
            {/* Leyenda del mapa */}
            {establecimientos.length > 1 && (
              <div className="mt-6 flex justify-center gap-6 flex-wrap">
                {establecimientos.map((est, index) => (
                  <div key={est.id} className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                      {index === 0 ? 'M' : index}
                    </div>
                    <span className="text-gray-700 font-medium">
                      {index === 0 ? 'Matriz' : `Sucursal ${index}`}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}
    </PublicLayout>
  );
};

export default RestaurantesPage;
